import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/api/query/client';
import { AppProvider, useAppContext } from '@/contexts/AppContext';
import { DrawerProvider } from '@/contexts/DrawerContext';
import { MainLayout } from '@/layouts/MainLayout';
import { AuthLayout } from '@/layouts/AuthLayout';
import { PrivateRouter } from './PrivateRouter';

// Lazy load pages for code splitting
const Login = lazy(() => import('@/pages/Login'));
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const MemberManagement = lazy(() => import('@/pages/members/MemberManagement'));
const PaymentManagement = lazy(() => import('@/pages/payments/PaymentManagement'));
const LoanManagement = lazy(() => import('@/pages/loans/LoanManagement'));
const BalanceManagement = lazy(() => import('@/pages/balances/BalanceManagement'));

// Loading component
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

function AppRoutes() {
  const { isAuthenticated } = useAppContext();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
            <AuthLayout>
              <Login />
            </AuthLayout>
            )
          }
        />

        {/* Protected Routes */}
        <Route
          path="/dashboard"
          element={
            <PrivateRouter>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </PrivateRouter>
          }
        />
        <Route
          path="/members"
          element={
            <PrivateRouter>
              <MainLayout>
                <MemberManagement />
              </MainLayout>
            </PrivateRouter>
          }
        />
        <Route
          path="/payments"
          element={
            <PrivateRouter>
              <MainLayout>
                <PaymentManagement />
              </MainLayout>
            </PrivateRouter>
          }
        />

        <Route
          path="/loans"
          element={
            <PrivateRouter>
              <MainLayout>
                <LoanManagement />
              </MainLayout>
            </PrivateRouter>
          }
        />

        <Route
          path="/balances"
          element={
            <PrivateRouter>
              <MainLayout>
                <BalanceManagement />
              </MainLayout>
            </PrivateRouter>
          }
        />

        {/* Default redirect */}
        <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />
        
        {/* 404 - redirect to dashboard or login */}
        <Route path="*" element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} />
      </Routes>
    </Suspense>
  );
}

export function AppRouter() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppProvider>
          <DrawerProvider>
            <AppRoutes />
          </DrawerProvider>
        </AppProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
